import { useSelector } from "react-redux";
import { get } from "lodash";
// @ts-expect-error: ignoring as it is existing js file
import { applicationSelector } from "@/store/slices/applicationFormSlice";
import type { ApplicationFormSlice, ApplicationState } from "@/store/slices/types/applicationFormSlice";
import {
  getIsAssetLoan,
  getIsConsumerOrPersonalApplication,
  getIsCommercialOrBusinessApplication,
  searchableAssets,
} from "@/constants";
import type {
  LenderProduct,
  RemainingDataFields,
} from "@/providers/LenderCardContextProvider/types/LenderProductTypes";
import { getProgressStatus } from "@/utils/getProgressStatus";
import { useConditionalFields } from "./useConditionalFields";
import {
  getIsRequiredFieldEmpty,
  getMissingPersonalFinanceItemIndex,
  personalFinanceLists,
} from "./utils/requiredFieldValues";

// These loan detail fields are either derived or not actionable from the required-fields flow.
const excludedLoanDetailsFields = new Set([
  "amortStartDate",
  "brokerageFinanced",
  "cash",
  "inArrears",
  "lender",
  "commission",
  "brokerageAdjustment",
  "fees",
  "repaymentStructure",
  "repaymentFrequency",
  "payout",
  "deposit",
  "tradeIn",
  "loanAmount",
  "rateAdjustment",
  "rate",
  "repayments",
]);

const isIncludedLoanDetailsField = (field: string | undefined): field is string => {
  if (!field) {
    return false;
  }

  const fieldName = field.split("_isInvalid=")[0];
  return !excludedLoanDetailsFields.has(fieldName);
};

const getIndexedFieldDetails = ({
  field,
  listName,
  hasDotBeforeIndex = true,
}: {
  field: string;
  listName: string;
  hasDotBeforeIndex?: boolean;
}) => {
  const fieldBaseKey = field.split(`${listName}.[0].`)[1] ?? field.split(`${listName}[0].`)[1];
  const indexedFieldName = hasDotBeforeIndex ? `${listName}.[0]` : `${listName}[0]`;
  const fieldKey = fieldBaseKey ? `${indexedFieldName}.${fieldBaseKey}` : field;

  return {
    fieldBaseKey,
    fieldKey,
  };
};

const getIndexedFieldValue = ({
  item,
  rawFieldName,
  index,
}: {
  item: Record<string, unknown> | undefined;
  rawFieldName: string;
  index: string;
}) => {
  const sanitisedRawFieldName = rawFieldName.replace("[0]", "").replace(/\.\./, ".");
  const [outerField, ...innerFieldPathParts] = sanitisedRawFieldName.split(".");
  const list = get(item, outerField ?? "");
  const listItem = Array.isArray(list) ? list[Number(index)] : undefined;
  return get(listItem, innerFieldPathParts.join("."));
};

const useProductRequiredFields = (product: LenderProduct | undefined) => {
  const { applicationType, asset, customers, introducerDeclarationAgree, entities, loanDetails } = useSelector<
    ApplicationFormSlice,
    ApplicationState
  >(applicationSelector);
  const isAssetLoan = getIsAssetLoan(applicationType);
  const isConsumerOrPersonalApplication = getIsConsumerOrPersonalApplication(applicationType);
  const isCommercialOrBusinessApplication = getIsCommercialOrBusinessApplication(applicationType);
  const { rawRequiredFields } = product ?? {};
  const {
    agreement: agreementConditionalFields,
    asset: assetConditionalFields,
    customer: customerConditionalFields,
    entity: entityConditionalFields,
    loanDetails: loanDetailsConditionalFields,
  } = useConditionalFields(product?.conditionalFields);

  // Asset required fields.
  const getAssetFields = () => {
    if (isAssetLoan) {
      const assetFields = rawRequiredFields?.asset ?? [];
      const getAssetRequiredFields = () => {
        // If application has manual asset or an asset type that is not searchable.
        if (asset?.isManualAssets || !searchableAssets.includes(asset.assetType ?? "")) {
          const fieldsToReplace = ["glassesMake", "glassesModel"];
          const fieldsToRemove = ["glassesVariant"];
          const updatedAssetFields = assetFields.map((field) => {
            if (fieldsToReplace.includes(field)) {
              return field.replace("glasses", "").toLowerCase();
            }
            if (fieldsToRemove.includes(field)) {
              return undefined;
            }
            return field;
          });
          // Deduplicate and filter undefined values.
          return [...new Set(updatedAssetFields)].filter((field) => field);
        } else {
          const fieldsToReplace = ["make", "model", "variant"];
          const updatedAssetFields = assetFields.map((field) => {
            if (fieldsToReplace.includes(field)) {
              const fieldName = (field ?? "").charAt(0).toUpperCase() + field.slice(1);
              return `glasses${fieldName}`;
            }
            return field;
          });
          // Always add glassesVariant to the list.
          updatedAssetFields.push("glassesVariant");
          // Deduplicate and filter undefined values.
          return [...new Set(updatedAssetFields)].filter((field) => field);
        }
      };
      const assetRequiredFields = getAssetRequiredFields();

      // Filter out fields that have been populated.
      const remainingAssetRequiredFields = assetRequiredFields
        .filter((field) => {
          return getIsRequiredFieldEmpty(get(asset, field ?? ""));
        })
        .filter((field) => field !== undefined);
      return { assetRequiredFields, remainingAssetRequiredFields };
    }
    return undefined;
  };

  // Customer required fields.
  const getCustomerFields = () => {
    const customerFields = rawRequiredFields?.customer ?? [];
    // If there is no customer in the application but there are required fields for customer.
    if (customers?.length === 0 && customerFields.length > 0) {
      return { customerRequiredFields: ["customerRequired"], remainingCustomerRequiredFields: ["customerRequired"] };
    }
    // Append customerId to the field.
    // Some inconsistent field names are being returned. Example customerProfile.maritalStatus vs maritalStatus.
    const inconsistentFieldNames = [
      "residencyStatus",
      "maritalStatus",
      "contactNumber",
      "rentAmount",
      "livingArrangements",
      "shareOfExpenses",
      "numberOfDependants",
    ];
    const customerRequiredFields = customerFields
      .flatMap((field) =>
        customers.map((customer) => {
          // If customer has not populated city, then remove monthsAt and yearsAt required fields.
          const addressCityField = get(customer, "addresses[0].city");
          if (!addressCityField) {
            const fieldsToRemove = ["addresses[0].monthsAt", "addresses[0].yearsAt"];
            if (fieldsToRemove.includes(field)) {
              return undefined;
            }
          }
          // If addresses are populated, then check if the customer has entered addresses for at least 36 months.
          if ((customer.addresses ?? []).length > 0 && field.includes("addresses")) {
            const months = customer.addresses?.reduce((acc, item) => {
              const totalMonths = item.yearsAt * 12 + item.monthsAt;
              return acc + totalMonths;
            }, 0);
            if (months < 36) {
              return "addressIsAtLeast36Months";
            }
          }
          if ((customer.addresses ?? []).length === 0 && field.includes("addresses")) {
            return "customerAddress";
          }
          if (field === "customerProfile.numberOfDependants" || field === "numberOfDependants") {
            return undefined;
          }
          // Some inconsistent field names are being returned. Example customerAssets[0]. vs customerAssets.[0].
          const personalFinanceList = personalFinanceLists.find((listName) => field.includes(listName));
          if (personalFinanceList) {
            const { fieldBaseKey, fieldKey } = getIndexedFieldDetails({
              field,
              listName: personalFinanceList,
              // The Assets component reads its errors as customerAssets[0].field, without the dot before the index.
              hasDotBeforeIndex: personalFinanceList !== "customerAssets",
            });
            // Point at the first validated row missing the field. When none is, the first row shows it is filled in.
            const itemIndex = getMissingPersonalFinanceItemIndex({
              listName: personalFinanceList,
              items: customer[personalFinanceList] ?? [],
              fieldPath: fieldBaseKey ?? "",
            });
            return `${fieldKey}_${customer._id}_${itemIndex >= 0 ? itemIndex : 0}`;
          }
          if (field.includes("employers")) {
            const { fieldBaseKey, fieldKey } = getIndexedFieldDetails({ field, listName: "employers" });
            const employersIndex = (customer?.employers ?? []).findIndex((item) =>
              getIsRequiredFieldEmpty(get(item, fieldBaseKey ?? "")),
            );
            return `${fieldKey}_${customer._id}_${employersIndex >= 0 ? employersIndex : 0}`;
          }
          if (inconsistentFieldNames.includes(field)) {
            return `customerProfile.${field}_${customer._id}`;
          }
          return `${field}_${customer._id}`;
        }),
      )
      .filter((field) => field !== undefined);
    const deduplicatedCustomerRequiredFields = [...new Set(customerRequiredFields)];

    // Filter out fields that have been populated, finding by customer id and field name.
    const remainingCustomerRequiredFields = deduplicatedCustomerRequiredFields
      .filter((field) => {
        const rawFieldName = field.split("_")[0];
        const customerId = field.split("_")[1];
        const listOfFieldValuesIndex = field.split("_")[2];
        const findCustomerById = customers.find((customer) => customer._id === customerId);
        if (
          rawFieldName === "emails?.slice(-1)[0].address" &&
          (findCustomerById?.emails ?? []).find((email) => email.status === "current")?.address
        ) {
          return undefined;
        }
        if (
          rawFieldName === "phoneNumbers?.slice(-1)[0].number" &&
          (findCustomerById?.phoneNumbers ?? []).find((phoneNumber) => phoneNumber.status === "current")?.number
        ) {
          return undefined;
        }
        if (listOfFieldValuesIndex !== undefined) {
          const fieldValue = getIndexedFieldValue({
            item: findCustomerById as unknown as Record<string, unknown> | undefined,
            rawFieldName,
            index: listOfFieldValuesIndex,
          });
          return getIsRequiredFieldEmpty(fieldValue);
        }
        const fieldValue = get(findCustomerById, rawFieldName ?? "");
        return getIsRequiredFieldEmpty(fieldValue);
      })
      .filter((field) => field !== undefined);

    return { customerRequiredFields: deduplicatedCustomerRequiredFields, remainingCustomerRequiredFields };
  };

  // Agreement required fields.
  const getAgreementFields = () => {
    const agreementRequiredFields = rawRequiredFields?.agreement ?? [];

    const remainingAgreementRequiredFields = agreementRequiredFields.filter((field) => {
      if (field === "introducerDeclarationAgree") {
        return !introducerDeclarationAgree;
      } else {
        const primaryCustomerDisclaimer = customers?.[0]?.customerDisclaimer;
        return getIsRequiredFieldEmpty(get(primaryCustomerDisclaimer, field ?? ""));
      }
    });

    return { agreementRequiredFields, remainingAgreementRequiredFields };
  };

  // Entity required fields.
  const getEntityFields = () => {
    const entityFields = rawRequiredFields?.entity ?? [];
    const unavailableFields = ["phoneNumbers?.slice(-1)[0].number", "addresses[0].streetType"];

    // If there is no entities in the application but there are required fields for entities.
    if ((entities ?? []).length === 0 && entityFields.length > 0) {
      return { entityRequiredFields: ["entityRequired"], remainingEntityRequiredFields: ["entityRequired"] };
    }

    const entityRequiredFields = entityFields
      .flatMap((field) => {
        return entities.map((entity) => {
          if ((entity.addresses ?? []).length === 0 && field.includes("addresses")) {
            return `entityAddress_${entity._id}`;
          }
          if (unavailableFields.includes(field)) {
            return undefined;
          }
          return `${field}_${entity._id}`;
        });
      })
      .filter((field) => field !== undefined);

    const remainingEntityRequiredFields = entityRequiredFields.filter((field) => {
      const rawFieldName = field.split("_")[0];
      const entityId = field.split("_")[1];
      const findEntityById = entities.find((entity) => entity._id === entityId);
      return getIsRequiredFieldEmpty(get(findEntityById, rawFieldName ?? ""));
    });
    return { entityRequiredFields, remainingEntityRequiredFields };
  };

  // Loan details required fields.
  const getLoanDetailsFields = () => {
    const loanDetailsFields = (rawRequiredFields?.loanDetails ?? []).filter(isIncludedLoanDetailsField);
    const remainingLoanDetailsFields = loanDetailsFields
      .filter((field) => {
        return getIsRequiredFieldEmpty(get(loanDetails, field ?? ""));
      })
      .filter((field) => field !== undefined);
    return { loanDetailsFields, remainingLoanDetailsFields };
  };

  // Consolidate fields.
  // Asset.
  const { assetRequiredFields = [], remainingAssetRequiredFields = [] } = getAssetFields() ?? {};
  const consolidatedAllAssetFields = [...assetRequiredFields, ...assetConditionalFields.all];
  const consolidatedRemainingAssetFields = [...remainingAssetRequiredFields, ...assetConditionalFields.remaining];

  // Customer.
  const { customerRequiredFields = [], remainingCustomerRequiredFields = [] } = getCustomerFields() ?? {};
  // Until an employer exists, show one actionable section requirement for each customer.
  const consolidateEmployerSections = (fields: string[]) => {
    const sectionRequirements = new Set<string>();
    return fields
      .map((field) => {
        const [fieldName, customerId] = field.split("_");
        if (!/^employers(?:\.|\[|$)/.test(fieldName)) {
          return field;
        }
        const customer = customers.find((item) => item._id === customerId);
        return customer && (customer.employers ?? []).length === 0 ? `employerRequired_${customerId}` : field;
      })
      .filter((field) => {
        if (!field.startsWith("employerRequired_")) {
          return true;
        }
        if (sectionRequirements.has(field)) {
          return false;
        }
        sectionRequirements.add(field);
        return true;
      });
  };
  const consolidatedAllCustomerFields = consolidateEmployerSections([
    ...customerRequiredFields,
    ...customerConditionalFields.all,
  ]);
  const consolidatedRemainingCustomerFields = consolidateEmployerSections([
    ...remainingCustomerRequiredFields,
    ...customerConditionalFields.remaining,
    // Conditional field checks can return no missing rows for an empty employer array.
    ...consolidatedAllCustomerFields.filter((field) => field.startsWith("employerRequired_")),
  ]);

  // Agreement.
  const { agreementRequiredFields = [], remainingAgreementRequiredFields = [] } = getAgreementFields() ?? {};
  const consolidatedAllAgreementFields = [...agreementRequiredFields, ...agreementConditionalFields.all];
  const consolidatedRemainingAgreementFields = [
    ...remainingAgreementRequiredFields,
    ...agreementConditionalFields.remaining,
  ];

  // Entity.
  const { entityRequiredFields = [], remainingEntityRequiredFields = [] } = getEntityFields() ?? {};
  const consolidatedAllEntityFields = [...entityRequiredFields, ...entityConditionalFields.all];
  const consolidatedRemainingEntityFields = [...remainingEntityRequiredFields, ...entityConditionalFields.remaining];

  // Loan details.
  const { loanDetailsFields, remainingLoanDetailsFields } = getLoanDetailsFields() ?? {};
  // Conditional validations can also target derived fields, so filter again after merging both sources.
  const consolidatedAllLoanDetailsFields = [...loanDetailsFields, ...loanDetailsConditionalFields.all].filter(
    isIncludedLoanDetailsField,
  );
  const consolidatedRemainingLoanDetailsFields = [
    ...remainingLoanDetailsFields,
    ...loanDetailsConditionalFields.remaining,
  ].filter(isIncludedLoanDetailsField);

  const allRequiredFields = [
    ...(isAssetLoan ? consolidatedAllAssetFields : []),
    ...consolidatedAllCustomerFields,
    ...(isConsumerOrPersonalApplication ? consolidatedAllAgreementFields : []),
    ...(isCommercialOrBusinessApplication ? consolidatedAllEntityFields : []),
    ...consolidatedAllLoanDetailsFields,
  ];
  const allRemainingRequiredFields = [
    ...(isAssetLoan ? consolidatedRemainingAssetFields : []),
    ...consolidatedRemainingCustomerFields,
    ...(isConsumerOrPersonalApplication ? consolidatedRemainingAgreementFields : []),
    ...(isCommercialOrBusinessApplication ? consolidatedRemainingEntityFields : []),
    ...consolidatedRemainingLoanDetailsFields,
  ];

  const fieldsTotalLength = allRequiredFields.length;
  const fieldsDoneLength = fieldsTotalLength - allRemainingRequiredFields.length;
  const fieldsCheckedPercentage = (fieldsDoneLength / fieldsTotalLength) * 100;

  const remainingFields: RemainingDataFields = {
    asset: isAssetLoan ? consolidatedRemainingAssetFields : [],
    customer: consolidatedRemainingCustomerFields,
    agreement: isConsumerOrPersonalApplication ? consolidatedRemainingAgreementFields : [],
    entity: isCommercialOrBusinessApplication ? consolidatedRemainingEntityFields : [],
    loanDetails: consolidatedRemainingLoanDetailsFields,
  };

  const isFieldsCompleted = fieldsCheckedPercentage === 100;

  return {
    remainingFields,
    fieldsTotalLength,
    fieldsCheckedPercentage,
    fieldsDoneLength,
    fieldsStatus: getProgressStatus(fieldsCheckedPercentage),
    isFieldsCompleted,
  };
};

export { useProductRequiredFields };
